import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFilterDependencies } from '../../hooks/useFilterDependencies';
import { RootState } from '../../store';
import { 
  Colors, 
  Spacing, 
  FontSizes, 
  FontWeights, 
  BorderRadius, 
  Shadows 
} from '../../constants/theme';
import {
  FilterDimension,
  FilterValue,
  setFilter,
  addFilterValue,
  removeFilterValue,
  clearFilter,
  clearAllFilters,
  setDateRange,
  toggleDrawer,
  toggleSection,
  setSearchQuery,
  saveView,
  loadView,
  deleteView,
  selectActiveFilters,
  selectDateRange,
  selectFilteredCount,
  selectIsPrivacyProtected,
  selectSavedViews,
  selectIsDrawerOpen,
} from '../../store/enhancedFiltersSlice';
import { 
  FilterIcon, 
  CalendarIcon, 
  CheckIcon, 
  ArrowRightIcon,
  ClockIcon 
} from '../icons/CorporateIcons';
import { hasPermission } from '../../utils/roleBasedAccess';

// Styled Components
const DrawerOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  z-index: 999;
`;

const DrawerContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: ${props => props.isOpen ? 0 : '-400px'};
  width: 400px;
  height: 100vh;
  background: ${Colors.surface};
  box-shadow: ${Shadows.xl};
  transition: right 0.3s ease;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const DrawerHeader = styled.div`
  padding: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DrawerTitle = styled.h2`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${Colors.textSecondary};
  font-size: ${FontSizes.xl}px;
  cursor: pointer;
  padding: ${Spacing.xs}px;
  
  &:hover {
    color: ${Colors.textPrimary};
  }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${Spacing.lg}px;
`;

const DrawerFooter = styled.div`
  padding: ${Spacing.lg}px;
  border-top: 1px solid ${Colors.border};
  display: flex;
  gap: ${Spacing.md}px;
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: ${Spacing.lg}px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  padding-left: ${Spacing.xl + 16}px;
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.md}px;
  background: ${Colors.background};
  
  &:focus {
    outline: none;
    border-color: ${Colors.primary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${Spacing.md}px;
  top: 50%;
  transform: translateY(-50%);
  color: ${Colors.textTertiary};
`;

const FilterSection = styled.div`
  margin-bottom: ${Spacing.xl}px;
`;

const SectionHeader = styled.button<{ expanded: boolean }>`
  width: 100%;
  padding: ${Spacing.md}px;
  background: ${Colors.background};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
`;

const SectionBadge = styled.span`
  background: ${Colors.primary};
  color: ${Colors.textOnPrimary};
  padding: 2px 8px;
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
`;

const ExpandIcon = styled.div<{ expanded: boolean }>`
  transform: rotate(${props => props.expanded ? '90deg' : '0deg'});
  transition: transform 0.2s ease;
  color: ${Colors.textSecondary};
`;

const SectionContent = styled.div<{ expanded: boolean }>`
  max-height: ${props => props.expanded ? '400px' : '0'};
  overflow: ${props => props.expanded ? 'visible' : 'hidden'};
  transition: max-height 0.3s ease;
  margin-top: ${props => props.expanded ? Spacing.md + 'px' : '0'};
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  cursor: pointer;
  border-radius: ${BorderRadius.sm}px;
  transition: background 0.2s ease;
  
  &:hover {
    background: ${Colors.backgroundDark};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-right: ${Spacing.sm}px;
  accent-color: ${Colors.primary};
`;

const OptionLabel = styled.span`
  flex: 1;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
`;

const OptionCount = styled.span`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
`;

const ActiveFiltersSection = styled.div`
  margin-bottom: ${Spacing.lg}px;
  padding: ${Spacing.md}px;
  background: ${Colors.backgroundDark};
  border-radius: ${BorderRadius.md}px;
`;

const ActiveFiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.sm}px;
`;

const ActiveFiltersTitle = styled.div`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ClearAllButton = styled.button`
  background: none;
  border: none;
  color: ${Colors.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const FilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.round}px;
  margin-right: ${Spacing.xs}px;
  margin-bottom: ${Spacing.xs}px;
`;

const ChipLabel = styled.span`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textPrimary};
`;

const ChipRemove = styled.button`
  background: none;
  border: none;
  color: ${Colors.textSecondary};
  cursor: pointer;
  padding: 0;
  font-size: ${FontSizes.sm}px;
  
  &:hover {
    color: ${Colors.danger};
  }
`;

const DateRangeSection = styled.div`
  padding: ${Spacing.md}px;
  background: ${Colors.backgroundDark};
  border-radius: ${BorderRadius.md}px;
  margin-bottom: ${Spacing.lg}px;
`;

const DatePresets = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${Spacing.sm}px;
  margin-top: ${Spacing.md}px;
`;

const PresetButton = styled.button<{ active?: boolean }>`
  padding: ${Spacing.sm}px;
  background: ${props => props.active ? Colors.primary : Colors.surface};
  color: ${props => props.active ? Colors.textOnPrimary : Colors.textPrimary};
  border: 1px solid ${props => props.active ? Colors.primary : Colors.border};
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? Colors.primaryDark : Colors.surfaceHover};
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: ${Spacing.md}px;
  background: ${props => props.variant === 'primary' ? Colors.primary : Colors.surface};
  color: ${props => props.variant === 'primary' ? Colors.textOnPrimary : Colors.textPrimary};
  border: 1px solid ${props => props.variant === 'primary' ? Colors.primary : Colors.border};
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.variant === 'primary' ? Colors.primaryDark : Colors.surfaceHover};
  }
`;

const PrivacyWarning = styled.div`
  padding: ${Spacing.md}px;
  background: ${Colors.warning}20;
  border: 1px solid ${Colors.warning}40;
  border-radius: ${BorderRadius.md}px;
  margin-bottom: ${Spacing.lg}px;
`;

const WarningText = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.warning};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
`;

// Filter configuration
const FILTER_CONFIG: Record<FilterDimension, { 
  label: string; 
  icon?: React.ReactNode;
  rbacPermission?: string;
}> = {
  date: { label: 'Date Range', icon: <CalendarIcon size={16} /> },
  country: { label: 'Country/Region' },
  region: { label: 'Region' },
  businessUnit: { label: 'Business Unit' },
  department: { label: 'Department' },
  function: { label: 'Function' },
  level: { label: 'Level' },
  grade: { label: 'Grade' },
  employmentType: { label: 'Employment Type' },
  manager: { label: 'Manager' },
  team: { label: 'Team' },
  gender: { label: 'Gender', rbacPermission: 'canViewSensitiveMetrics' },
  tenureBand: { label: 'Tenure Band' },
  performanceBand: { label: 'Performance Band', rbacPermission: 'canViewSensitiveMetrics' },
  attritionRiskBand: { label: 'Attrition Risk', rbacPermission: 'canViewAttritionPredictions' },
};

interface EnhancedFilterDrawerProps {
  onApply?: () => void;
}

const EnhancedFilterDrawer: React.FC<EnhancedFilterDrawerProps> = ({ onApply }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isOpen = useSelector(selectIsDrawerOpen);
  const activeFilters = useSelector(selectActiveFilters);
  const dateRange = useSelector(selectDateRange);
  const savedViews = useSelector(selectSavedViews);
  const filteredCount = useSelector(selectFilteredCount);
  const isPrivacyProtected = useSelector(selectIsPrivacyProtected);
  const currentRole = useSelector((state: RootState) => state.user.currentRole);
  const expandedSections = useSelector((state: RootState) => 
    state.enhancedFilters.expandedSections
  );
  const searchQuery = useSelector((state: RootState) => 
    state.enhancedFilters.searchQuery
  );
  
  const { 
    loadInitialFilterValues, 
    validateFilterSelection,
    clearCascadingFilters 
  } = useFilterDependencies();
  
  // Load initial filter values when drawer opens
  useEffect(() => {
    if (isOpen) {
      loadInitialFilterValues();
    }
  }, [isOpen, loadInitialFilterValues]);

  // Check RBAC permissions for filters
  const visibleFilters = useMemo(() => {
    return Object.entries(FILTER_CONFIG).filter(([dimension, config]) => {
      if (!config.rbacPermission) return true;
      return hasPermission(currentRole, config.rbacPermission as any);
    });
  }, [currentRole]);

  const handleClose = () => {
    dispatch(toggleDrawer());
  };

  const handleClearAll = () => {
    dispatch(clearAllFilters());
  };

  const handleApply = () => {
    if (onApply) {
      onApply();
    }
    handleClose();
  };

  const handleToggleSection = (dimension: FilterDimension) => {
    dispatch(toggleSection(dimension));
  };

  const handleFilterChange = (
    dimension: FilterDimension, 
    value: string, 
    checked: boolean
  ) => {
    // Validate selection against dependencies
    if (checked && !validateFilterSelection(dimension, value)) {
      console.warn(`Invalid selection for ${dimension}: ${value}`);
      return;
    }
    
    if (checked) {
      dispatch(addFilterValue({ dimension, value }));
    } else {
      dispatch(removeFilterValue({ dimension, value }));
    }
  };

  const handleRemoveFilter = (dimension: FilterDimension, value: string) => {
    dispatch(removeFilterValue({ dimension, value }));
  };

  const handleDatePreset = (preset: string) => {
    // Implementation would set appropriate date ranges
    console.log('Date preset:', preset);
  };

  // Get active filter count
  const activeFilterCount = Object.values(activeFilters).reduce(
    (count, filter) => count + (filter?.values.length || 0), 
    0
  );

  return (
    <>
      <DrawerOverlay isOpen={isOpen} onClick={handleClose} />
      <DrawerContainer isOpen={isOpen}>
        <DrawerHeader>
          <DrawerTitle>
            <FilterIcon size={20} />
            Filters
            {activeFilterCount > 0 && (
              <SectionBadge>{activeFilterCount}</SectionBadge>
            )}
          </DrawerTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </DrawerHeader>

        <DrawerBody>
          {/* Privacy Warning */}
          {isPrivacyProtected && (
            <PrivacyWarning>
              <WarningText>
                <span>⚠</span>
                Results limited to protect privacy (less than 5 records)
              </WarningText>
            </PrivacyWarning>
          )}

          {/* Search */}
          <SearchBar>
            <SearchIcon>🔍</SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search filter values..."
              value={searchQuery}
              onChange={(e) => dispatch(setSearchQuery(e.target.value))}
            />
          </SearchBar>

          {/* Active Filters */}
          {activeFilterCount > 0 && (
            <ActiveFiltersSection>
              <ActiveFiltersHeader>
                <ActiveFiltersTitle>Active Filters</ActiveFiltersTitle>
                <ClearAllButton onClick={handleClearAll}>
                  Clear All
                </ClearAllButton>
              </ActiveFiltersHeader>
              {Object.entries(activeFilters).map(([dimension, filter]) => 
                filter?.values.map(value => (
                  <FilterChip key={`${dimension}-${value}`}>
                    <ChipLabel>
                      {FILTER_CONFIG[dimension as FilterDimension].label}: {value}
                    </ChipLabel>
                    <ChipRemove 
                      onClick={() => handleRemoveFilter(dimension as FilterDimension, value)}
                    >
                      ×
                    </ChipRemove>
                  </FilterChip>
                ))
              )}
            </ActiveFiltersSection>
          )}

          {/* Date Range */}
          <DateRangeSection>
            <SectionTitle>
              <CalendarIcon size={16} />
              Date Range
            </SectionTitle>
            <DatePresets>
              <PresetButton 
                active={dateRange.preset === 'lastMonth'}
                onClick={() => handleDatePreset('lastMonth')}
              >
                Last Month
              </PresetButton>
              <PresetButton 
                active={dateRange.preset === 'lastQuarter'}
                onClick={() => handleDatePreset('lastQuarter')}
              >
                Last Quarter
              </PresetButton>
              <PresetButton 
                active={dateRange.preset === 'ytd'}
                onClick={() => handleDatePreset('ytd')}
              >
                Year to Date
              </PresetButton>
              <PresetButton 
                active={dateRange.preset === 'custom'}
                onClick={() => handleDatePreset('custom')}
              >
                Custom Range
              </PresetButton>
            </DatePresets>
          </DateRangeSection>

          {/* Filter Sections */}
          {visibleFilters.map(([dimension, config]) => {
            if (dimension === 'date') return null; // Handled separately
            
            const filter = activeFilters[dimension as FilterDimension];
            const isExpanded = expandedSections.includes(dimension as FilterDimension);
            const selectedCount = filter?.values.length || 0;

            return (
              <FilterSection key={dimension}>
                <SectionHeader
                  expanded={isExpanded}
                  onClick={() => handleToggleSection(dimension as FilterDimension)}
                >
                  <SectionTitle>
                    {config.label}
                    {selectedCount > 0 && (
                      <SectionBadge>{selectedCount}</SectionBadge>
                    )}
                  </SectionTitle>
                  <ExpandIcon expanded={isExpanded}>
                    <ArrowRightIcon size={16} />
                  </ExpandIcon>
                </SectionHeader>

                <SectionContent expanded={isExpanded}>
                  {/* Display available values from the store */}
                  {filter?.availableValues && filter.availableValues.length > 0 ? (
                    filter.availableValues
                      .filter(option => 
                        !searchQuery || 
                        option.label.toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map(option => (
                        <FilterOption key={option.id}>
                          <Checkbox
                            type="checkbox"
                            checked={filter?.values.includes(option.id) || false}
                            disabled={option.disabled}
                            onChange={(e) => handleFilterChange(
                              dimension as FilterDimension,
                              option.id,
                              e.target.checked
                            )}
                          />
                          <OptionLabel 
                            style={{ 
                              opacity: option.disabled ? 0.5 : 1,
                              textDecoration: option.disabled ? 'line-through' : 'none' 
                            }}
                          >
                            {option.label}
                          </OptionLabel>
                          {option.count !== undefined && (
                            <OptionCount>
                              {option.disabled ? '<5' : `(${option.count})`}
                            </OptionCount>
                          )}
                        </FilterOption>
                      ))
                  ) : (
                    <FilterOption>
                      <OptionLabel style={{ color: Colors.textTertiary, fontStyle: 'italic' }}>
                        Loading filter options...
                      </OptionLabel>
                    </FilterOption>
                  )}
                </SectionContent>
              </FilterSection>
            );
          })}
        </DrawerBody>

        <DrawerFooter>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleApply}>
            Apply Filters ({filteredCount} results)
          </Button>
        </DrawerFooter>
      </DrawerContainer>
    </>
  );
};

export default EnhancedFilterDrawer;